import { useCallback, useState } from 'react';
import { App } from 'antd';
import type { MenuProps } from 'antd';
import {
  BankOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  LinkOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDeletePaymentMutation, type Payment } from '../api';
import { extractApiErrorPayload, getApiErrorMessage } from '@/utils/apiError';
import useAppNotification from '@/hooks/useAppNotification';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { isPaymentSettled } from './paymentStatus';

/** Duration (ms) to show the "Copied!" feedback before reverting. */
const COPY_FEEDBACK_DURATION_MS = 2000;

/**
 * Row actions of the payments table: which payment each modal (details,
 * gateway status, refund) is open for, copy-URL feedback, delete
 * confirmation, and the grouped "Actions" dropdown items for a row.
 *
 * @param onRefresh - Reloads the list after a delete
 */
export function usePaymentRowActions(onRefresh: () => void) {
  const { t } = useTranslation('payment-module');
  const { modal } = App.useApp();
  const { showSuccessMessage, showErrorMessage } = useAppNotification();
  const deletePaymentMutation = useDeletePaymentMutation();
  const { copy, copiedKey: copiedPaymentUrl } = useCopyToClipboard(COPY_FEEDBACK_DURATION_MS);

  const [detailPayment, setDetailPayment] = useState<Payment | null>(null);
  const [gatewayStatusPayment, setGatewayStatusPayment] = useState<Payment | null>(null);
  const [refundPayment, setRefundPayment] = useState<Payment | null>(null);

  /** Closes the payment detail modal. */
  const closeDetails = useCallback(() => setDetailPayment(null), []);
  /** Closes the gateway status modal. */
  const closeGatewayStatus = useCallback(() => setGatewayStatusPayment(null), []);
  /** Closes the refund modal. */
  const closeRefund = useCallback(() => setRefundPayment(null), []);

  /** Copies a payment URL to the clipboard with brief visual feedback. */
  const copyPaymentUrl = useCallback(
    async (paymentUrl: string, paymentId: number) => {
      await copy(paymentUrl, {
        key: paymentId,
        successMessage: t('actions.copySuccess'),
        errorMessage: t('actions.copyError'),
      });
    },
    [copy, t],
  );

  /** Confirms and deletes an expired/unpaid payment link. */
  const confirmDelete = useCallback(
    (payment: Payment) => {
      modal.confirm({
        title: t('actions.deleteConfirmTitle'),
        icon: <ExclamationCircleOutlined />,
        content: t('actions.deleteConfirmDescription'),
        okText: t('actions.deleteConfirmOk'),
        cancelText: t('actions.deleteConfirmCancel'),
        okButtonProps: { danger: true },
        onOk: () =>
          new Promise<void>((resolve, reject) => {
            deletePaymentMutation.mutate(payment.token, {
              onSuccess: () => {
                showSuccessMessage(t('actions.deleteSuccess'));
                onRefresh();
                resolve();
              },
              onError: (error) => {
                const { status } = extractApiErrorPayload(error);
                if (status === 404) {
                  showErrorMessage(t('actions.deleteNotFound'));
                  onRefresh();
                  resolve();
                  return;
                }
                showErrorMessage(
                  getApiErrorMessage(
                    error,
                    status === 422 ? t('actions.deleteNotAllowed') : t('actions.deleteError'),
                  ),
                );
                reject();
              },
            });
          }),
      });
    },
    [modal, deletePaymentMutation, t, showSuccessMessage, showErrorMessage, onRefresh],
  );

  /** Builds the grouped dropdown items (view / actions) for a given payment row. */
  const getActionMenuItems = useCallback(
    (record: Payment): MenuProps['items'] => {
      const status = record.status.toLowerCase();
      const copied = copiedPaymentUrl === record.id;

      const actionItems = [
        // Hide "Copy Payment URL" for completed and refunded payments
        ...(!isPaymentSettled(status)
          ? [
              {
                key: 'copy',
                icon: copied ? <CheckCircleOutlined /> : <LinkOutlined />,
                label: copied ? t('actions.copied') : t('actions.copyPaymentUrl'),
                onClick: () => copyPaymentUrl(record.paymentUrl, record.id),
              },
            ]
          : []),
        ...(status === 'completed'
          ? [
              {
                key: 'refund',
                icon: <RollbackOutlined />,
                label: t('actions.processRefund'),
                danger: true,
                onClick: () => setRefundPayment(record),
              },
            ]
          : []),
        ...(record.policy?.canBeDeleted
          ? [
              {
                key: 'delete',
                icon: <DeleteOutlined />,
                label: t('actions.deletePayment'),
                danger: true,
                onClick: () => confirmDelete(record),
              },
            ]
          : []),
      ];

      return [
        {
          key: 'view-group',
          type: 'group' as const,
          label: t('actions.view'),
          children: [
            {
              key: 'details',
              icon: <EyeOutlined />,
              label: t('actions.paymentDetails'),
              onClick: () => setDetailPayment(record),
            },
            ...(status !== 'pending'
              ? [
                  {
                    key: 'gateway',
                    icon: <BankOutlined />,
                    label: t('actions.gatewayStatus'),
                    onClick: () => setGatewayStatusPayment(record),
                  },
                ]
              : []),
          ],
        },
        // Only include the actions group when there is at least one action
        ...(actionItems.length > 0
          ? [{ key: 'actions-group', type: 'group' as const, label: t('actions.actions'), children: actionItems }]
          : []),
      ];
    },
    [copiedPaymentUrl, copyPaymentUrl, confirmDelete, t],
  );

  return {
    getActionMenuItems,
    copiedPaymentUrl,
    copyPaymentUrl,
    detailPayment,
    closeDetails,
    gatewayStatusPayment,
    closeGatewayStatus,
    refundPayment,
    closeRefund,
  };
}
